import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SharedUsersList } from "./SharedUsersList";

interface SharePlaceDialogProps {
  placeId: number;
  onShare?: () => void;
  children: React.ReactNode;
}

interface Profile {
  id: string;
  active: boolean;
  email: string;
}

export function SharePlaceDialog({ placeId, onShare, children }: SharePlaceDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleShare = async () => {
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter an email address.",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        variant: "destructive",
        title: "Invalid email",
        description: "Please enter a valid email address.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Check if trying to share with self
      if (email.toLowerCase() === user.email?.toLowerCase()) {
        toast({
          variant: "destructive",
          title: "Invalid recipient",
          description: "You cannot share with yourself.",
        });
        return;
      }

      // First find the user by email
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, active, email')
        .eq('email', email.toLowerCase())
        .limit(1)
        .single<Profile>();

      if (profileError) {
        console.error('Profile lookup error:', profileError);
        throw new Error("Error looking up user");
      }
      
      if (!profiles) {
        toast({
          variant: "destructive",
          title: "User not found",
          description: "No user found with this email address. They need to sign up first.",
        });
        return;
      }

      // Check if user is active
      if (profiles.active === false) {
        toast({
          variant: "destructive",
          title: "Inactive user",
          description: "This user's account is currently inactive.",
        });
        return;
      }

      // Check if already shared
      const { data: existingShare } = await supabase
        .from("shared_assets")
        .select()
        .eq('place_id', placeId)
        .eq('shared_type', 'place')
        .eq('shared_with', profiles.id)
        .limit(1);

      if (existingShare && existingShare.length > 0) {
        toast({
          variant: "destructive",
          title: "Already shared",
          description: "This place has already been shared with this user.",
        });
        return;
      }

      const shareData = {
        place_id: placeId,
        shared_by: user.id,
        shared_type: 'place',
        shared_with: profiles.id,
      };

      const { error: shareError } = await supabase
        .from("shared_assets")
        .insert(shareData);

      if (shareError) {
        console.error('Share error:', shareError);
        if (shareError.code === '23505') {
          toast({
            variant: "destructive",
            title: "Already shared",
            description: "This place has already been shared with this user.",
          });
          return;
        }
        throw new Error("Error sharing place");
      }

      toast({
        title: "Success",
        description: "Place shared successfully",
      });
      
      setEmail("");
      if (onShare) onShare();
    } catch (error: any) {
      console.error('Share error:', error);
      toast({
        variant: "destructive",
        title: "Error sharing place",
        description: error.message || "An unexpected error occurred",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setEmail("");
      }}
    >
      <div onClick={() => setIsOpen(true)}>
        {children}
      </div>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Place</DialogTitle>
          <DialogDescription>
            Enter the email address of the user you want to share this place with.
            They must have an account to receive the share.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Share with Email</label>
            <Input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          
          <Button
            onClick={handleShare}
            disabled={loading}
            className="w-full"
          >
            {loading ? "Sharing..." : "Share Place"}
          </Button>

          <div className="pt-4 border-t">
            <SharedUsersList placeId={placeId} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}